import { useEffect, useRef, useState } from 'react';

/** Debounce a fast-changing value (search boxes) so we do not query on every keystroke. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    const prev = document.title;
    document.title = title ? `${title} · Business Portal` : 'Business Portal';
    return () => void (document.title = prev);
  }, [title]);
}

/** Warn before leaving the page with unsaved changes. */
export function useBeforeUnload(active: boolean) {
  const ref = useRef(active);
  ref.current = active;
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (!ref.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);
}

export function useLocalStorage<T>(key: string, initial: T): [T, (v: T) => void] {
  const [val, setVal] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setVal(v);
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* private mode: remembering is a convenience only */
    }
  };
  return [val, set];
}
